export { Grid } from './grid'
export { RowActionList } from './row-action-list'
export { LoadingListCellRenderer } from './loading-list-cell-renderer'
export {
  doFiltersHaveOptions,
  doFiltersHaveSelectionsOnGroups
} from './column-header'

import {
  FilterConfig,
  OnGroupToggleProps,
  OnOptionToggleProps,
  OnMultiGroupToggleProps
} from '../filter'
import { FilterConfigs, FilterSelections } from '../filter/filters'
import {
  defaultBucketToID,
  Bucket,
  AggregationConfigs
} from '../../components/aggregations'
import { MessageDescriptor } from 'react-intl'

export interface MaxCellHeightsByRow {
  readonly [key: string]: number
}

export interface ColumnHeaderConfig<TAggregationCategory extends string> {
  readonly message?: MessageDescriptor
  readonly messageClassName?: string
  readonly aggregations?: TAggregationCategory[]
  readonly render?: CustomColumnHeaderRenderer<TAggregationCategory>
  readonly isBeta?: boolean
}

export type OnGroupToggleFn<TAggregationCategory extends string> = (
  props: OnGroupToggleProps<TAggregationCategory>
) => void

export type OnMultiGroupToggleFn<TAggregationCategory extends string> = (
  props: OnMultiGroupToggleProps<TAggregationCategory>
) => void

export type OnOptionToggleFn<TAggregationCategory extends string> = (
  props: OnOptionToggleProps<TAggregationCategory>
) => void

export interface ColumnHeaderFilters<TAggregationCategory extends string> {
  readonly groupConfigs: FilterConfigs
  readonly selections?: FilterSelections
  readonly groupIDs: string[]
  readonly onMultiGroupToggle?: OnMultiGroupToggleFn<TAggregationCategory>
  readonly onGroupToggle?: OnGroupToggleFn<TAggregationCategory>
  readonly onOptionToggle?: OnOptionToggleFn<TAggregationCategory>
}

export interface ColumnHeaderContentRendererProps {
  readonly isClickable: boolean
  readonly isOpen: boolean
  readonly onClear?: () => void
  readonly isFilterable: boolean
  readonly isFiltered: boolean
}

export type ColumnHeaderContentRenderer = (
  props: ColumnHeaderContentRendererProps
) => React.ReactElement<any>

export interface CustomColumnHeaderRendererProps<
  TAggregationCategory extends string
> {
  readonly renderContent: ColumnHeaderContentRenderer
  readonly filters?: ColumnHeaderFilters<TAggregationCategory>
  readonly clearAll?: () => void
}

export type CustomColumnHeaderRenderer<TAggregationCategory extends string> = (
  props: CustomColumnHeaderRendererProps<TAggregationCategory>
) => React.ReactElement<any>

export interface ColumnHeaderProps<TAggregationCategory extends string>
  extends ColumnHeaderConfig<TAggregationCategory> {
  readonly filters?: ColumnHeaderFilters<TAggregationCategory>
  readonly clearAll?: () => void
}

export type ColumnHeaderRenderer<TAggregationCategory extends string> = (
  props: ColumnHeaderProps<TAggregationCategory> &
    ColumnHeaderConfig<TAggregationCategory>
) => React.ReactNode

export interface CellContentProps {
  readonly className?: string
}

export interface CellProps<TData, Props = {}> {
  readonly rowData: TData
  readonly getCellContentProps: (props: Props) => CellContentProps & Props
  readonly onCellHeightChange?: (height: number) => void
}

export type CellRenderer<TData> = (props: CellProps<TData>) => React.ReactNode

export interface Column<TData, TAggregationCategory extends string> {
  readonly headerConfig?: ColumnHeaderConfig<TAggregationCategory>
  readonly cellRenderer: CellRenderer<TData>
  readonly loadingCellRenderer: () => React.ReactNode
  readonly width: number
  readonly minWidth?: number
  readonly maxWidth?: number
  readonly grow?: number
  readonly horizontalCenter?: boolean
  readonly isHidden?: () => boolean
}

export interface Aggregations {
  readonly [key: string]: Bucket[]
}

export function getAggregationFilters({
  aggregations,
  aggregationConfigs
}: {
  readonly aggregations?: Aggregations
  readonly aggregationConfigs?: AggregationConfigs<string>
}): FilterConfigs {
  const groupConfigs = {}
  for (const category in aggregationConfigs) {
    if (aggregationConfigs.hasOwnProperty(category)) {
      const {
        groupName,
        groupDisplayName,
        bucketToID,
        bucketToString,
        bucketToTooltipContent,
        bucketToGroupIndex
      } = aggregationConfigs[category]
      const filterConfig: FilterConfig = {
        id: category,
        groupName,
        groupDisplayName,
        optionToString: bucketToString ? bucketToString : defaultBucketToID,
        optionToID: bucketToID ? bucketToID : defaultBucketToID,
        optionToTooltipContent: bucketToTooltipContent,
        optionToGroupIndex: bucketToGroupIndex,
        options: aggregations ? aggregations[category] : []
      }

      Object.assign(groupConfigs, {
        [category]: filterConfig
      })
    }
  }
  return groupConfigs
}
